/**
 * src/lib/services/recovery.ts
 * One-time-code forgot-password and change-email. Talks only to auth-service via
 * the gateway; SMS goes through the Injozi OTP Service server-side and the browser
 * never sees its credentials.
 *
 * Forgot password is unauthenticated and deliberately gives no hint whether the
 * account exists: requestResetCode always resolves the same way. Behaviour is keyed
 * off HTTP status because client.ts only surfaces string `detail` messages.
 */

import { post } from '@/lib/api/client';

export type RecoveryChannel = 'email' | 'sms';

export interface ResetCodeSent {
  detail: string;
  resend_after_seconds: number;
}

export interface EmailChangeCodeSent {
  sent_to: string;
  channel: RecoveryChannel;
  resend_after_seconds: number;
}

export const requestResetCode = (email: string, channel: RecoveryChannel) =>
  post<ResetCodeSent>('/api/v1/auth/password/reset/code/request', { email, channel });

/** Resolves with a short-lived, single-use token for confirmPasswordReset(). */
export const verifyResetCode = (email: string, channel: RecoveryChannel, code: string) =>
  post<{ reset_token: string }>('/api/v1/auth/password/reset/code/verify', { email, channel, code });

/**
 * The code goes to the CURRENT email address (default) or by SMS to the mobile number saved
 * on the account; the new address is held server-side until the code is confirmed.
 */
export const requestEmailChangeCode = (newEmail: string, password: string, channel: RecoveryChannel = 'email') =>
  post<EmailChangeCodeSent>('/api/v1/auth/email/change/code/request', { new_email: newEmail, password, channel });

export const confirmEmailChangeCode = (code: string) =>
  post<{ message: string }>('/api/v1/auth/email/change/code/confirm', { code });
